import React, { useContext, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    OutlinedInput,
    Checkbox,
    Grid,
} from '@mui/material';
import { useBookingTypeContext } from './bookingTypeProvider';
import { useSelectedBusiness } from '../../../../../platform/redux/store';
import GenericTooltip from '../../../../../platform/components/common/genericToolTip';
import { StyledTypography } from '../../../../../platform/assets/css/styledComponent';
import { BpCheckedIcon, BpIcon } from '../../../../../platform/util/bPcheckbox';
import { UserContext } from '../../../../../platform/components/auth/auth-context';
import { rootClientPath } from '../../../../../platform/util/link';
import { Link, useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
const tooltipData = {
    link: "Enter a bookingType link that describes your bookingType. If the provided link already exists, the system will automatically generate a unique link to prevent conflicts",
    group: "One-to-one or group appointment",
    price: `This will be enabled if you set your payment integration at your business page. 
            <a href="${rootClientPath}/biz/business" target="_blank" style="color: blue; text-decoration: underline;">
            Click here to configure payments</a>`,
};


const BookingBasicPart2 = (props) => {
    const {
        isDisabled,
        errors,
    } = useBookingTypeContext();
    const { bookingDetails, handleBookingDetailChange } = props
    const navigate = useNavigate();
    const selectedBusiness = useSelectedBusiness();
    const businessUrl = selectedBusiness?.businessBasic?.url || "";
    const userContext = useContext(UserContext);
    const providerUser = userContext.providerUser;
    const isPaymentMethodSelected = providerUser.isPaymentMethodSelected;
    function BpCheckbox(props) {
        return (
            <Checkbox
                disabled={isDisabled}
                sx={{
                    padding: 0,
                    "&:hover": { bgcolor: "transparent" },
                }}
                disableRipple
                color="default"
                checkedIcon={<BpCheckedIcon />}
                icon={<BpIcon />}
                checked={bookingDetails.remaining}
                onChange={() =>
                    handleBookingDetailChange("remaining", !bookingDetails.remaining)
                }
                inputProps={{ "aria-label": "Checkbox demo" }}
            />

        );
    }
    const prefix = `${window.location.origin}/${businessUrl}/${providerUser.url}/`;

    return (
        <Box mt={6}>
            {/* Booking URL Section */}
            <Box mt={2}>
                <Box display="flex" alignItems="center" mt={2}>
                    <StyledTypography>Booking URL*</StyledTypography>
                    <GenericTooltip title={tooltipData.link} />
                </Box>
                <Box display="flex" flexDirection="column" gap={1} mt={1}>
                    <Box display="flex" alignItems="center">
                        <StyledTypography mr={1}>{prefix}</StyledTypography>
                        <OutlinedInput
                            disabled={isDisabled}
                            onChange={(e) =>
                                handleBookingDetailChange("link", e.target.value.toLowerCase().replace(/\s+/g, '-'))
                            }
                            value={bookingDetails.link}
                            placeholder="Enter booking URL"
                            fullWidth
                            aria-describedby="link-error-text"
                            sx={{
                                flex: 1,
                                maxWidth: '500px',
                                height: '35px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                    borderBottom: '1px solid lightgray',
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Booking Size Options */}
            <Grid container alignItems="center" mt={3}>
                <RadioGroup value={bookingDetails.bookingSize}
                    onChange={(e) =>
                        handleBookingDetailChange("bookingSize", e.target.value)
                    }
                    row>
                    <FormControlLabel value="one" control={<Radio />} label="One-to-One" />
                    <FormControlLabel value="group" control={<Radio />} label="Group" />
                </RadioGroup>
                <GenericTooltip title={tooltipData.group} />
            </Grid>

            {/* Additional Options for Group Booking */}
            {bookingDetails.bookingSize === "group" && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Row for Max Spots */}
                        <Box display="flex" alignItems="center" flexDirection="row" gap={2}>
                            <StyledTypography variant="h6">Max spots for invitees</StyledTypography>
                            <GenericTooltip title={tooltipData.maxInvitees} />
                            <OutlinedInput
                                disabled={isDisabled}
                                onChange={(e) =>
                                    handleBookingDetailChange("maxNum", e.target.value)
                                }
                                value={bookingDetails.maxNum < 1 ? 1 : bookingDetails.maxNum}
                                type="number"
                                inputProps={{ min: 0 }}
                                sx={{
                                    flex: 1,
                                    maxWidth: '100px',
                                    height: '35px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                        borderBottom: '2px solid lightgray',
                                    },
                                }}
                            />
                        </Box>

                        {/* New Row for Checkbox and Typography */}
                        <Box display="flex" alignItems="center" flexDirection="row" gap={1}>
                            <BpCheckbox disabled={isDisabled} />
                            <Typography>Display remaining slots on booking page.</Typography>
                        </Box>
                    </Box>
                </>
            )}
            <Divider sx={{ my: 4 }} />
            <Box display="flex" alignItems="center">
                <StyledTypography>Price (USD)  </StyledTypography>
                <GenericTooltip title={tooltipData.price} />
                <OutlinedInput
                    disabled={isDisabled}
                    onChange={(e) =>
                        handleBookingDetailChange("price", e.target.value)
                    }
                    onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value)) {
                            // Rounds and fixes the price to 2 decimals.
                            handleBookingDetailChange("price", value.toFixed(2));
                        }
                    }}
                    value={bookingDetails.price}
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    sx={{
                        flex: 1,
                        maxWidth: '150px',
                        height: '35px',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                            borderBottom: '2px solid lightgray', // Only bottom border
                        },
                    }}
                />
            </Box>
            <Box
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            // Reflect the current bookingDetails state.
                            checked={bookingDetails.isPaymentRequired}
                            // Disable the checkbox if no payment method is selected.
                            disabled={!isPaymentMethodSelected}
                            // Toggle the isPaymentRequired flag only if a payment method is selected.
                            onClick={() => {
                                if (isPaymentMethodSelected) {
                                    handleBookingDetailChange("isPaymentRequired", !bookingDetails.isPaymentRequired)
                                }
                            }}
                        />
                    }
                    label="Payment Required to Book"
                />
                {/* If the payment method is not selected, display a red warning message */}
                {!isPaymentMethodSelected && (
                    <Typography variant="body2" style={{ color: 'red', marginTop: 4 }}>
                        Payment method not set up.{' '}
                        <Link component={RouterLink} to="/biz/business/3" underline="hover">
                            Click here to set it up.
                        </Link>
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default BookingBasicPart2;
